import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import DriverList from "./DriverList";
import ShipmentsList from "./ShipmentsList";
import { toast } from "react-toastify";
import { logger } from "../../helper/logger";
import LeafletMap from "./LeafletMap";
import {
	LOAD_DRIVERS,
	LOAD_ORDERS_FOR_MAP,
} from "../../api/queries/Queries";
import { stagingOptions } from "../../constants/apollo";
import { useSearchParams } from "react-router-dom";
import {
	GET_ALL_CITIES_FOR_FILTER,
	GET_ALL_MERCHANTS_FOR_FILTER,
	GET_ALL_HUBS_FOR_FILTER,
	GET_CITY_FOR_FILTER,
	GET_MERCHANT_FOR_FILTER
} from "../../api/queries/FilterQueries";
import {
	FETCH_ALL_SHIFT_TEMPLATES,
	FETCH_SHIFT_TEMPLATE
} from "../../api/mutations/ShiftManagementMutations";
import { getDriverFetchFilters, getOrderFetchFilters, getShipmentFetchFilters } from "../../helper/getFetchFilters";
import { defFilters } from "constants/filters";
import { useAssignCourier } from "./hooks/useAssignCourier";
import { useMergeOrder } from "./hooks/useMergeOrder";
import { useUnassignCourier } from "./hooks/useUnassignCourier";
import MapIsTypingContext from "context/MapIsTypingContext";
import { getColorFromTrackingNumber, point1Lat, point1Lng, point2Lat, point2Lng, centerLat, centerLng, zoom } from "constants/map";
import { useOrdersMapStore } from "./store/useOrdersMapStore";

export default function OrdersMap() {
	const localStorageFilters = JSON.parse(
		window?.localStorage?.getItem("mapPageFilters") || "{}"
	);
	const [searchParams, setSearchParams] = useSearchParams(
		localStorageFilters || {}
	);
	const [allShipments, setAllShipments] = useState([])
	const [allDrivers, setAllDrivers] = useState([])
	const [merchants, setMerchants] = useState([])
	const [hubs, setHubs] = useState([])
	const [cities, setCities] = useState([])
	const [shipmentLoading, setShipmentLoading] = useState(false)
	const [shipmentListLoading, setShipmentListLoading] = useState(false)
	const [driverListLoading, setDriverListLoading] = useState(false)
	const [driverLoading, setDriverLoading] = useState(false)
	const [areaSelectRegion, setAreaSelectRegion] = useState(null)
	const [shipmentLoadingText, setShipmentLoadingText] = useState(null)
	const [driverLoadingText, setDriverLoadingText] = useState(null)

	const transformFilters = (filters) => {
		const normalizeArray = (value) => (Array.isArray(value) ? value : [value]);
		const getParamValues = (key, defaultValue = ["all"]) => {
			const values = searchParams.getAll(key);
			return values.length ? values : defaultValue;
		};
		return {
			...filters,
			shipmentType: getParamValues("shipmentType"),
			shipmentOrderStatus: getParamValues("shipmentOrderStatus"),
			hubIds: getParamValues("hubIds"),
			shipmentMerchant: getParamValues("shipmentMerchant"),
			shipmentPaymentMethod: getParamValues("shipmentPaymentMethod"),
			shipmentDateFrom: searchParams.get("shipmentDateFrom") ?? filters.shipmentDateFrom ?? "",
			shipmentDateTo: searchParams.get("shipmentDateTo") ?? filters.shipmentDateTo ?? "",
			driverCity: String(filters.driverCity),
			driverDevice: String(filters.driverDevice),
			driverVehicle: String(filters.driverVehicle),
			driverUniform: String(filters.driverUniform),
			driverStatus: String(filters.driverStatus),
			driverLastSeen: String(filters.driverLastSeen),
			onlyScheduledOrders: Boolean(filters.onlyScheduledOrders),
		};
	};

	const initialProductFilters = transformFilters({
		...defFilters,
		...localStorageFilters,
		...Object.fromEntries(searchParams),
	});
	const [getAllDrivers, { data: fetchedDrivers, error: driversFetchError }] = useLazyQuery(LOAD_DRIVERS, { ...stagingOptions, fetchPolicy: "network-only" });
	const [getAllShipments, { error: shipmentsFetchError }] = useLazyQuery(LOAD_ORDERS_FOR_MAP, { ...stagingOptions, fetchPolicy: "network-only" });
	const [getMerchants] = useLazyQuery(GET_ALL_MERCHANTS_FOR_FILTER, stagingOptions);
	const [getHubs] = useLazyQuery(GET_ALL_HUBS_FOR_FILTER, stagingOptions);
	const [getCities] = useLazyQuery(GET_ALL_CITIES_FOR_FILTER, stagingOptions);
	const [getMerchant] = useLazyQuery(GET_MERCHANT_FOR_FILTER, stagingOptions);
	const [getCity] = useLazyQuery(GET_CITY_FOR_FILTER, stagingOptions);
	const [getShiftTemplates] = useLazyQuery(FETCH_ALL_SHIFT_TEMPLATES, stagingOptions);
	const [getShiftTemplate] = useLazyQuery(FETCH_SHIFT_TEMPLATE, stagingOptions);
	const [shipmentCursor, setShipmentCursor] = useState(null)
	const [shipmentsHasNextPage, setShipmentsHasNextPage] = useState(false)
	const latestShipmentRequestId = useRef(0)
	const didRunInitialShipmentSearchEffect = useRef(false)
	const [driverSkip, setDriverSkip] = useState(0)
	const initialShipmentSearchText =
		Object.fromEntries([...searchParams]).shipmentSearchText || localStorageFilters?.shipmentSearchText || '';
	const initialDriverSearchText =
		Object.fromEntries([...searchParams]).driverSearchText || localStorageFilters?.driverSearchText || '';

	const [selectedDriver, setSelectedDriver] = useState(null);
	const selectedShipmentIds = useOrdersMapStore((state) => state.selectedShipmentIds);
	const setSelectedShipmentIds = useOrdersMapStore((state) => state.setSelectedShipmentIds);
	const selectedShipmentsById = useOrdersMapStore((state) => state.selectedShipmentsById);
	const syncSelectedSnapshots = useOrdersMapStore((state) => state.syncSelectedSnapshots);
	const selectedDriverId = useOrdersMapStore((state) => state.selectedDriverId);
	const setSelectedDriverId = useOrdersMapStore((state) => state.setSelectedDriverId);
	const productFilters = useOrdersMapStore((state) => state.productFilters);
	const setProductFilters = useOrdersMapStore((state) => state.setProductFilters);
	const shipmentSearchText = useOrdersMapStore((state) => state.shipmentSearchText);
	const setShipmentSearchText = useOrdersMapStore((state) => state.setShipmentSearchText);
	const driverSearchText = useOrdersMapStore((state) => state.driverSearchText);
	const setDriverSearchText = useOrdersMapStore((state) => state.setDriverSearchText);
	const mapBounds = useOrdersMapStore((state) => state.mapBounds);
	const setMapBounds = useOrdersMapStore((state) => state.setMapBounds);
	const mapCanter = useOrdersMapStore((state) => state.mapCanter);
	const setMapCanter = useOrdersMapStore((state) => state.setMapCanter);
	const onlyScheduledOrders = useOrdersMapStore((state) => state.onlyScheduledOrders);
	const setOnlyScheduledOrders = useOrdersMapStore((state) => state.setOnlyScheduledOrders);
	const [activeShipments, setActiveShipments] = useState([]);
	const [activeDrivers, setActiveDrivers] = useState([]);
	const [totalDriverCount, setTotalDriverCount] = useState(0)
	const [shipmentFilterUsed, setShipmentFilterUsed] = useState(false)
	const { handleAssignCourier } = useAssignCourier({ clearUp, selectedShipmentIds })
	// Keep a persistent snapshot of every selected shipment so panning the map
	// (which refetches allShipments for the new viewport) can't clear the
	// "Selected Orders" pane. Fresh data is folded in whenever an order is in view.
	useEffect(() => {
		syncSelectedSnapshots(allShipments, selectedShipmentIds);
	}, [allShipments, selectedShipmentIds, syncSelectedSnapshots]);

	const selectedShipments = useMemo(() => {
		const inView = new Map(allShipments.map((shipment) => [String(shipment?.id), shipment]));
		const total = selectedShipmentIds.length;
		return selectedShipmentIds
			.map((id, index) => {
				const key = String(id);
				// Prefer the persisted snapshot; fall back to the in-view list for the
				// first render after selecting, before the snapshot effect runs.
				const shipment = selectedShipmentsById[key] ?? inView.get(key);
				return shipment
					? {
						...shipment,
						number: total - index,
					}
					: null;
			})
			.filter(Boolean);
	}, [allShipments, selectedShipmentIds, selectedShipmentsById]);
	const { handleUnassignCourier } = useUnassignCourier({ clearUp, selectedShipmentIds, selectedShipments })
	const { handleMergeOrder } = useMergeOrder({ clearUp, setAllShipments, selectedShipments })
	const [isInputFocused, setIsInputFocused] = useState(false);

	const shipmentOrderStatus = searchParams.getAll("shipmentOrderStatus");
	const isOrderFiltersOpen =
		(shipmentOrderStatus.length > 0 &&
			!(shipmentOrderStatus.length === 1 && shipmentOrderStatus[0] === "ready_for_delivery")) ||
		searchParams.getAll("shipmentReadySince").length > 0 ||
		Boolean(searchParams.get("shipmentDateFrom")) ||
		Boolean(searchParams.get("shipmentDateTo")) ||
		(!isNaN(parseInt(searchParams.get("startingDistance"), 10))) ||
		(!isNaN(parseInt(searchParams.get("endingDistance"), 10))) ||
		searchParams.getAll("shipmentMerchant").length > 0 ||
		searchParams.getAll("shipmentHubOpen").length > 0 ||
		searchParams.getAll("shipmentPaymentMethod").length > 0 ||
		searchParams.getAll("shipmentType").length > 0 ||
		searchParams.getAll("hubIds").length > 0;
	const driverLastSeen = searchParams.getAll("driverLastSeen");
	const isCourierFiltersOpen =
		(driverLastSeen.length > 0 && !(driverLastSeen.length === 1 && driverLastSeen[0] === "30")) ||
		searchParams.getAll("driverCity").length > 0 ||
		searchParams.getAll("shiftTemplateId").length > 0 ||
		searchParams.getAll("driverVehicle").length > 0 ||
		searchParams.getAll("driverDevice").length > 0 ||
		searchParams.getAll("driverStatus").length > 0;

	const handleQueryChange = (obj) => {
		// update router query based on implemented filters
		let mergedObj = { ...Object.fromEntries([...searchParams]), ...obj };
		["shipmentOrderStatus", "shipmentType", "hubIds", "shipmentMerchant"].forEach((key) => {
			const values = searchParams.getAll(key);
			if (values.length) {
				mergedObj[key] = values;
			}
		});
		let modified = {};
		Object.keys(mergedObj).map((key) => {
			if (mergedObj[key] != "all" && mergedObj[key] != "") {
				if (key in obj) {
					modified[key] = obj[key];
				} else {
					modified[key] = mergedObj[key];
				}
			}
		});

		for (let key in modified) {
			if (Array.isArray(modified[key])) {
				modified[key] = modified[key].filter(item => item !== 'all');
			}
		}

		Object.keys(modified).forEach((key) => {
			const value = modified[key];
			if (
				value === null ||
				value === "null" ||
				(typeof value === "number" && isNaN(value))
			) {
				delete modified[key];
			}
		});

		Object.assign(modified, { ...mapCanter, ...mapBounds });
		setSearchParams(modified);
		localStorage.setItem(
			"mapPageFilters",
			JSON.stringify(modified)
		);
	};

	const [cityIds, setCityIds] = useState([]);
	const [filters, setFilters] = useState({
		shipment: {
			apply: false,
			showOnMap: true,
			sort_order: "asc",
		},
		driver: {
			apply: false,
			showOnMap: true,
		},
		dropoff: {
			showOnMap: false
		}
	});
	const defaultBounds = {
		point1Lat: point1Lat,
		point1Long: point1Lng,
		point2Lat: point2Lat,
		point2Long: point2Lng,
	};
	const getBoundSearchParam = (params, defaultValue) => {
		const keys = Array.isArray(params) ? params : [params];
		for (const key of keys) {
			const value = parseFloat(searchParams.get(key));
			if (!isNaN(value)) {
				return value;
			}
		}
		return defaultValue;
	};
	const initialMapBounds = {
		point1Lat: getBoundSearchParam('point1Lat', defaultBounds.point1Lat),
		point1Long: getBoundSearchParam(['point1Long', 'point1Lng'], defaultBounds.point1Long),
		point2Lat: getBoundSearchParam('point2Lat', defaultBounds.point2Lat),
		point2Long: getBoundSearchParam(['point2Long', 'point2Lng'], defaultBounds.point2Long),
	};
	const initialMapCanter = {
		mapCanterLat: getBoundSearchParam(['mapCanterLat', 'mapCenterLat'], centerLat),
		mapCanterLng: getBoundSearchParam(['mapCanterLng', 'mapCenterLng'], centerLng),
		zoom: getBoundSearchParam(['zoom', 'mapZoom'], 12),
	};
	const initialOnlyScheduledOrders = searchParams.get('onlyScheduledOrders') || false;
	const handleMapBoundsChange = (lat1, long1, lat2, long2, mapCanter, zoom) => {
		setMapBounds({
			point1Lat: lat1,
			point1Long: long1,
			point2Lat: lat2,
			point2Long: long2,
		});
		setMapCanter({
			mapCanterLat: mapCanter.lat,
			mapCanterLng: mapCanter.lng,
			zoom: zoom,
		});
	}
	useEffect(() => {
		setProductFilters(initialProductFilters);
		setMapBounds(initialMapBounds);
		setMapCanter(initialMapCanter);
		setShipmentSearchText(initialShipmentSearchText);
		setDriverSearchText(initialDriverSearchText);
		setOnlyScheduledOrders(initialOnlyScheduledOrders);
	}, []);

	useEffect(() => {
		let filt = JSON.parse(
			window?.localStorage?.getItem("mapPageFilters") || "{}"
		);
		handleQueryChange({ onlyScheduledOrders, ...filt, ...mapCanter });
	}, [mapCanter]);

	const cleanFilters = (filters) => {
		const updatedFilters = { ...filters };
		Object.keys(updatedFilters).forEach((key) => {
			if (Array.isArray(updatedFilters[key])) {
				updatedFilters[key] = updatedFilters[key].filter(value => value !== "all");
			}
		});
		return updatedFilters;
	};

	const updateShipmentsFromResponse = (fetchAllOrders, append = false) => {
		const allShipmentsList = [...(fetchAllOrders?.items || [])];

		setShipmentCursor(fetchAllOrders?.nextCursor || null);
		setShipmentsHasNextPage(Boolean(fetchAllOrders?.hasNextPage));

		try {
			allShipmentsList.forEach((shipment) => {
				if (shipment && shipment.shipment && shipment.shipment.trackingNo) {
					shipment.color = getColorFromTrackingNumber(shipment.shipment.trackingNo);
				} else {
					shipment.color = "red";
				}
			});
		} catch (error) {
			console.error("Error processing shipments:", error);
		}

		setAllShipments((prev) => append ? [...prev, ...allShipmentsList] : allShipmentsList)
		if (shipmentFilterUsed) {
			setShipmentFilterUsed(false)
		}
	};

	const fetch_shipments = ({
		cursor = null,
		search = shipmentSearchText,
		filters = productFilters,
		load = true,
		limit = 250,
		isShipmentFilterModalActive = false,
		append = false,
	} = {}) => {
		try {
			setShipmentListLoading(true)
			if (load) {
				setShipmentLoading(true)
			}
			if (load == "twice") {
				setShipmentLoading(true)
			}

			const filterObj = getOrderFetchFilters(cleanFilters(filters), isShipmentFilterModalActive);
			let variables = {
				after: cursor,
				query: search,
				limit,
				...mapBounds,
				...filterObj,
			}
			// Remove map bounds when searching
			if (search !== "") {
				variables = {
					after: cursor,
					query: search,
					limit,
					...filterObj,
				}
			}
			const shipmentRequestId = latestShipmentRequestId.current + 1;
			latestShipmentRequestId.current = shipmentRequestId;
			return getAllShipments({
				variables: variables
			}).then((data) => {
				if (shipmentRequestId !== latestShipmentRequestId.current) return;
				const fetchedOrders = data?.data?.fetchAllOrders;
				updateShipmentsFromResponse(fetchedOrders, append);
				setShipmentListLoading(false)
				setShipmentLoading(false)
				if (fetchedOrders?.items?.length != 0) {
					setShipmentLoadingText('Rendering shipments!');
				} else {
					setShipmentLoadingText('No shipments found!');
				}
			}).catch((e) => {
				if (shipmentRequestId !== latestShipmentRequestId.current) return;
				toast.error("Error while loading shipments:\n", e?.message)
				setShipmentLoading(false)
				setShipmentListLoading(false)
			})
		}
		catch (e) {
			toast.error("Error while loading shipments:\n", e?.message)
			setShipmentLoading(false)
			setShipmentListLoading(false)
		}
	}

	useEffect(() => {
		setShipmentListLoading(false)
		setShipmentLoading(false)
	}, [shipmentsFetchError])

	useEffect(() => {
		setDriverListLoading(false)
		setDriverLoading(false)
	}, [driversFetchError])

	const fetch_drivers = ({
		skip = driverSkip,
		search = driverSearchText,
		filters = productFilters,
		load = true
	} = {}) => {
		try {
			setDriverListLoading(true)
			if (load) {
				setDriverLoading(true)
			}

			let filterObj = getDriverFetchFilters(filters);
			// logger(">>>FILTERS", filters, "\n>>>FILTRATION", filterObj)

			let updatedMapBounds = {
				sw: `${mapBounds.point1Lat}, ${mapBounds.point1Long}`,
				ne: `${mapBounds.point2Lat}, ${mapBounds.point2Long}`
			}
			let variables = {
				offset: skip,
				query: driverSearchText,
				// The map needs EVERY courier in the viewport. With a 30-row page the
				// backend's distance-from-centre DESC ordering returned only the farthest
				// (edge) couriers and dropped everyone near the centre. Fetch a generous
				// in-view page instead of a 30-row list page; with the backend now sorting
				// nearest-first this covers the centre outward. Raise if dense areas need more.
				limit: 100,
                order: "asc",
				...updatedMapBounds,
				// Default the map to online couriers. Offline couriers report stale
				// last-known positions and just crowd the edges. A user-selected status filter overrides this,
                // because filterObj is spread afterwards and sets isOnline for free/busy/offline.
				isOnline: true,
				...filterObj,
			}

			// Remove map bounds when searching
			if (driverSearchText && driverSearchText !== "") {
				variables = {
					offset: skip,
					query: driverSearchText,
					...filterObj,
				}
			}
			getAllDrivers({
				variables: variables
			}).then((data) => {
				setDriverListLoading(false)
				setDriverLoading(false)
				if (data?.data?.fetchAllCouriers?.items.length != 0) {
					setDriverLoadingText('Rending drivers!')
				} else {
					setDriverLoadingText('No drivers found!')
				}
			})
		}
		catch (e) {
			toast.error("Error while loading drivers:\n", e?.message)
			setDriverLoading(false)
			setDriverListLoading(false)
		}

	};

	useEffect(() => {
		if (fetchedDrivers) {
			const allDriversList = fetchedDrivers.fetchAllCouriers?.items;
			setTotalDriverCount(fetchedDrivers.fetchAllCouriers?.totalCount)
			if (driverSkip > 0) {
				setAllDrivers((prev) => [
					...prev,
					...allDriversList,
				])

			} else {
				setAllDrivers(allDriversList)
			}

			setDriverLoading(false)
		}
	}, [fetchedDrivers])

	const fetch_merchants = ({ page = 1, searchQuery } = {}) => {
		logger("fetch_merchants", page, searchQuery)

		return getMerchants({
			variables: {
				offset: (page - 1) * 20,
				query: searchQuery
			}
		}).then((res) => {
			setMerchants(res?.data?.fetchAllMerchants)

			return {
				options: res?.data?.fetchAllMerchants,
				hasMore: res?.data?.fetchAllMerchants?.length >= 1,
				additional: {
					page: searchQuery ? 2 : page + 1,
				}
			}
		});
	}

	const fetch_hubs = ({ page = 1, searchQuery, merchantId } = {}) => {

		return getHubs({
			variables: {
				offset: (page - 1) * 20,
				query: searchQuery,
				merchantId: +merchantId,
			}
		}).then((res) => {
			setHubs(res?.data?.fetchAllHubs)
			return {
				options: res?.data?.fetchAllHubs,
				hasMore: res?.data?.fetchAllHubs?.length >= 20,
				additional: {
					page: searchQuery ? 2 : page + 1,
				}
			}
		});
	}

	const fetch_merchant = ({ id } = {}) => {
		return getMerchant({
			variables: {
				id: parseInt(id)
			}
		}).then((res) => {
			let data = res?.data?.fetchMerchant
			return data
		});
	}

	const fetch_cities = ({ page = 1, searchQuery } = {}) => {
		logger("fetch_cities", page, searchQuery)
		return getCities({
			variables: {
				offset: (page - 1) * 20,
				query: searchQuery
			}
		}).then((res) => {
			setCities(res?.data?.fetchAllCities)
			return {
				options: res?.data?.fetchAllCities,
				hasMore: res?.data?.fetchAllCities?.length >= 1,
				additional: {
					page: searchQuery ? 2 : page + 1,
				}
			}
		});
	}

	const fetch_templates = ({ page = 1, searchQuery } = {}) => {
		return getShiftTemplates({
			variables: {
				limit: 20,
				offset: (page - 1) * 20,
				search: searchQuery
			}
		}).then((res) => {
			const items = res?.data?.fetchAllShiftTemplates?.items || [];
			const options = items.map((item) => ({
				value: item.id,
				label: item.name,
			}));

			return {
				options,
				hasMore: items.length >= 20,
				additional: {
					page: searchQuery ? 2 : page + 1,
				}
			};
		});
	};

	const fetch_template = ({ id } = {}) => {
		if (!id || id === "all") {
			return Promise.resolve({ value: "all", label: "All" });
		}

		return getShiftTemplate({
			variables: {
				id: id,
			}
		}).then((res) => {
			const data = res?.data?.fetchShiftTemplate;
			if (!data) {
				return { value: "all", label: "All" };
			}

			return {
				value: data.id,
				label: data.name,
			};
		});
	};

	const fetch_city = ({ id } = {}) => {
		return getCity({
			variables: {
				id: parseInt(id)
			}
		}).then((res) => {
			let data = res?.data?.fetchCity
			return data
		});
	}
	useEffect(() => {
		if (!didRunInitialShipmentSearchEffect.current) {
			didRunInitialShipmentSearchEffect.current = true;
			return;
		}
		fetch_shipments({ search: shipmentSearchText })
		handleQueryChange({ shipmentSearchText: shipmentSearchText });
		setShipmentFilterUsed(true)
	}, [shipmentSearchText])

	useEffect(() => {
		fetch_drivers({ search: driverSearchText })
		handleQueryChange({ driverSearchText: driverSearchText });
	}, [driverSearchText])

	function clearUp({ courier, orders }) {
		// Update the statuses based on the response
		setSelectedDriver(prevDriver => ({ ...prevDriver, isBusy: courier.isBusy }));
		setAllShipments((prevShipments) =>
			prevShipments.map((shipment) => {
				const updatedShipment = orders.find((order) => order.id === shipment.id);
				return updatedShipment
					? {
						...shipment,
						orderStatus: updatedShipment.orderStatus,
						courier: updatedShipment.courier || shipment.courier,
					}
					: shipment;
			})
		);

	}

	useEffect(() => {
		const data = JSON.parse(localStorage?.getItem("filtersData"));
		if (data?.shipment && data.driver) {
			setFilters(data);
		}
	}, []);

	useEffect(() => {
		localStorage?.setItem("filtersData", JSON.stringify(filters));
	}, [filters]);

	useEffect(() => {
		try {
			let drivers = [];
			selectedShipments.map((el) => {
				if (el && el.courier) {
					const index = drivers.findIndex((d) => d.id === el.courier.id);
					if (index === -1) drivers.push(el.courier);
				}
			});
			setActiveDrivers(drivers);
		} catch (e) {
			toast.error(e.message);
		}
	}, [selectedShipments]);

	useEffect(() => {
		if (filters.shipment.apply === true) {
			const fetchFilters = getShipmentFetchFilters(
				filters,
				shipmentSearchText
			);

			return setFilters((old) => ({
				...old,
				shipment: {
					...old.shipment,
					apply: false,
				},
			}));
		}
	}, [filters.shipment.apply]);

	return (
		<MapIsTypingContext.Provider value={{ isInputFocused, setIsInputFocused }}>
			<div className="flex  w-full bg-lightBgColor justify-between overflow-hidden" style={{ height: "98vh" }}>
				<div className={`w-[400px] max-w-[420px] pl-4 pr-2 pt-3`} style={{ height: "98vh" }}>
					<ShipmentsList
						filterUsed={shipmentFilterUsed}
						setFilterUsed={setShipmentFilterUsed}
						handleQueryChange={handleQueryChange}
						shipmentCursor={shipmentCursor}
						hasNextPage={shipmentsHasNextPage}
						fetch_hubs={fetch_hubs}
						fetch_merchants={fetch_merchants}
						fetch_merchant={fetch_merchant}
						fetch_shipments={fetch_shipments}
						fetch_cities={fetch_cities}
						fetch_city={fetch_city}
						setShipmentFilters={setProductFilters}
						shipmentFilters={productFilters}
						loading={shipmentLoading}
						shipmentListLoading={shipmentListLoading}
						cities={cities}
						merchants={merchants}
						hubs={hubs}
						activeShipments={activeShipments}
						mapBounds={mapBounds}
						shipments={allShipments}
						selectedShipmentIds={selectedShipmentIds}
						setSelectedShipmentIds={setSelectedShipmentIds}
						selectedShipments={selectedShipments}
						searchText={shipmentSearchText}
						setSearchText={setShipmentSearchText}
						shipmentLoadingText={shipmentLoadingText}
						isFilterOpen={isOrderFiltersOpen}
						onlyScheduledOrders={onlyScheduledOrders}
						setOnlyScheduledOrders={setOnlyScheduledOrders}
					/>
				</div>
				<LeafletMap
					mapBounds={mapBounds}
					handleMapBoundsChange={handleMapBoundsChange}
					handleAssign={() => handleAssignCourier(selectedDriverId)}
					mergeOrdersToShipment={handleMergeOrder}
					handleUnassign={handleUnassignCourier}
					filters={filters}
					setFilters={setFilters}
					shipments={allShipments}
					drivers={allDrivers}
					selectedShipmentIds={selectedShipmentIds}
					selectedDriver={selectedDriver}
					selectedShipments={selectedShipments}
					setSelectedShipmentIds={setSelectedShipmentIds}
					selectedDriverId={selectedDriverId}
					setSelectedDriverId={setSelectedDriverId}
					setSelectedDriver={setSelectedDriver}
					loading={driverLoading}
					areaSelectRegion={areaSelectRegion}
					setAreaSelectRegion={setAreaSelectRegion}
					handleQueryChange={handleQueryChange}
				/>
				<div className={`w-[400px] max-w-[420px] pl-4 pr-2 pt-3`} style={{ height: "98vh" }}>
					<DriverList
						totalDriverCount={totalDriverCount}
						handleQueryChange={handleQueryChange}
						setDriverSkip={setDriverSkip}
						fetch_city={fetch_city}
						fetch_template={fetch_template}
						fetch_merchants={fetch_merchants}
						fetch_drivers={fetch_drivers}
						fetch_cities={fetch_cities}
						fetch_templates={fetch_templates}
						setDriverFilters={setProductFilters}
						driverFilters={productFilters}
						loading={driverLoading}
						driverListLoading={driverListLoading}
						activeDrivers={activeDrivers}
						drivers={allDrivers}
						setDrivers={setAllDrivers}
						setSelectedDriverId={setSelectedDriverId}
						selectedDriverId={selectedDriverId}
						setSelectedDriver={setSelectedDriver}
						selectedDriver={selectedDriver}
						cityIds={cityIds}
						mapBounds={mapBounds}
						searchText={driverSearchText}
						setSearchText={setDriverSearchText}
						driverLoadingText={driverLoadingText}
						isFilterOpen={isCourierFiltersOpen}
					/>
				</div>
			</div>
		</MapIsTypingContext.Provider>
	);
}
